import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { SnackbarService } from 'src/app/services/snackbar.sevice';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators } from 'src/app/shared/custom-validators';
import { ClinicalFeedbackConstants } from '../clinical-feedback-constants';
import { DateTime } from 'luxon';
import { GetClinicalFeedbackOrdersRequest } from '@app/api-module/control-centre-api-client';
import { DateAdapter } from '@angular/material/core';
import { DatePickerDateAdapter } from '@app/order/adapters/date-picker-date-adapter';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';

@Component({
    selector: 'app-search-orders',
    templateUrl: './search-orders.component.html',
    styleUrls: ['./search-orders.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: DateAdapter, useClass: DatePickerDateAdapter }]
})
export class SearchOrdersComponent implements OnInit, OnDestroy {
    @Output()
    submitSearch = new EventEmitter<GetClinicalFeedbackOrdersRequest>();
    @Output()
    clearSearch = new EventEmitter();

    searchForm!: UntypedFormGroup;
    clinicalFeedbackConstants = ClinicalFeedbackConstants;
    panelOpenState = true;

    private readonly subscription = new Subscription();

    constructor(
        private readonly snackbarService: SnackbarService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly authenticationService: AuthenticationService
    ) {}

    get form() {
        return this.searchForm.controls;
    }

    ngOnInit() {
        this.initForm();
        this.subscription.add(
            this.orgSiteSelectionService.getSitesSelected().subscribe({
                next: _ => this.onSearchClear()
            })
        );
    }

    initForm() {
        this.searchForm = new UntypedFormGroup(
            {
                patientFirstName: new UntypedFormControl(null, [
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_64),
                    Validators.pattern(ClinicalFeedbackConstants.NAME_PATTERN)
                ]),
                patientLastName: new UntypedFormControl(null, [
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_64),
                    Validators.pattern(ClinicalFeedbackConstants.NAME_PATTERN)
                ]),
                patientBirthDate: new UntypedFormControl(null, []),
                patientMrn: new UntypedFormControl(null, [
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_64),
                    Validators.pattern(ClinicalFeedbackConstants.MRN_PATTERN)
                ]),
                accessionNumber: new UntypedFormControl(null, [Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_64)]),
                studyDate: new UntypedFormControl(null, [])
            },
            [CustomValidators.atLeastOneOfAll(Validators.required)]
        );
    }

    onSearchSubmit() {
        if (this.searchForm.valid && this.searchForm.dirty) {
            let siteIds = this.orgSiteSelectionService.getSelectedSitesValue();
            if (
                this.currentUserHasOrgWideAccess() &&
                (siteIds.length > 1 || this.orgSiteSelectionService.getHasOnlyOneSiteFlag() || siteIds[0] === null)
            ) {
                siteIds = [];
            }

            const request = new GetClinicalFeedbackOrdersRequest({
                orgCode: this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode,
                siteIds: siteIds,
                patientFirstName: this.searchForm.value.patientFirstName,
                patientLastName: this.searchForm.value.patientLastName,
                patientDateOfBirth: DateTime.fromJSDate(this.searchForm.value.patientBirthDate).toISODate(),
                patientMrn: this.searchForm.value.patientMrn,
                accessionNumber: this.searchForm.value.accessionNumber,
                studyDateTime: DateTime.fromJSDate(this.searchForm.value.studyDate).toISODate()
            });
            this.submitSearch.emit(request);
        } else {
            this.searchForm.markAllAsTouched();
            this.snackbarService.openSnackBar(ClinicalFeedbackConstants.CANNOT_SEARCH_STUDY_MESSAGE, SnackbarService.SNACK_BAR_CLASS_ALERT);
        }
    }

    currentUserHasOrgWideAccess() {
        const currentUser = this.authenticationService.getCurrentUser();
        const allowedSites = currentUser.getAllowedSites(
            this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode,
            RoleEnum.ClinicalFeedback
        );

        return allowedSites?.length === 0;
    }

    onSearchClear() {
        this.searchForm.reset();
        this.clearSearch.emit();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
